'use client';

import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import type { CreatorExperienceId } from './experience-config';
import { normalizeWebsiteUrl, parseCreatorDestination, toQrContent } from './experience-config';

export function CreatorContentStep({
	experience,
	onValidityChange,
	defaultDynamic,
}: {
	experience: CreatorExperienceId;
	onValidityChange: (valid: boolean) => void;
	defaultDynamic: boolean;
}) {
	const { content, updateContent } = useQrCodeGeneratorStore((state) => state);
	const restored =
		content.type === 'url' ? parseCreatorDestination(experience, content.data.url) : null;
	const [url, setUrl] = useState(() =>
		experience === 'website' && restored && 'url' in restored ? restored.url : '',
	);
	const [phone, setPhone] = useState(() =>
		experience === 'whatsapp' && restored && 'phone' in restored ? restored.phone : '',
	);
	const [message, setMessage] = useState(() =>
		experience === 'whatsapp' && restored && 'message' in restored ? restored.message : '',
	);
	const [isDynamic, setIsDynamic] = useState(
		content.type === 'url' ? (content.data.isDynamic ?? defaultDynamic) : defaultDynamic,
	);
	const [error, setError] = useState('');

	// Wi-Fi fields
	const [wifiSsid, setWifiSsid] = useState(() =>
		content.type === 'wifi' ? content.data.ssid : '',
	);
	const [wifiPassword, setWifiPassword] = useState(() =>
		content.type === 'wifi' ? content.data.password : '',
	);
	const [wifiEncryption, setWifiEncryption] = useState<'WPA' | 'WEP' | 'nopass'>(() =>
		content.type === 'wifi' ? content.data.encryption : 'WPA',
	);

	// Event fields
	const [eventTitle, setEventTitle] = useState(() =>
		content.type === 'event' ? content.data.title : '',
	);
	const [eventLocation, setEventLocation] = useState(() =>
		content.type === 'event' ? (content.data.location ?? '') : '',
	);
	const [eventStartDate, setEventStartDate] = useState(() => {
		if (content.type === 'event' && content.data.startDate) {
			try {
				return new Date(content.data.startDate).toISOString().slice(0, 16);
			} catch {
				// fallback below
			}
		}
		return new Date(Date.now() + 3600000).toISOString().slice(0, 16);
	});
	const [eventEndDate, setEventEndDate] = useState(() => {
		if (content.type === 'event' && content.data.endDate) {
			try {
				return new Date(content.data.endDate).toISOString().slice(0, 16);
			} catch {
				// fallback below
			}
		}
		return new Date(Date.now() + 7200000).toISOString().slice(0, 16);
	});
	const [eventDescription, setEventDescription] = useState(() =>
		content.type === 'event' ? (content.data.description ?? '') : '',
	);

	// vCard fields
	const [vcardPhoto, setVcardPhoto] = useState(() =>
		content.type === 'vCard' ? (content.data.photo ?? '') : '',
	);
	const vcardPhotoInputRef = useRef<HTMLInputElement>(null);
	const [vcardFirstName, setVcardFirstName] = useState(() =>
		content.type === 'vCard' ? (content.data.firstName ?? '') : '',
	);
	const [vcardLastName, setVcardLastName] = useState(() =>
		content.type === 'vCard' ? (content.data.lastName ?? '') : '',
	);
	const [vcardCompany, setVcardCompany] = useState(() =>
		content.type === 'vCard' ? (content.data.company ?? '') : '',
	);
	const [vcardJob, setVcardJob] = useState(() =>
		content.type === 'vCard' ? (content.data.job ?? '') : '',
	);
	const [vcardPhoneMobile, setVcardPhoneMobile] = useState(() =>
		content.type === 'vCard' ? (content.data.phoneMobile ?? '') : '',
	);
	const [vcardPhoneBusiness, setVcardPhoneBusiness] = useState(() =>
		content.type === 'vCard' ? (content.data.phoneBusiness ?? '') : '',
	);
	const [vcardEmailBusiness, setVcardEmailBusiness] = useState(() =>
		content.type === 'vCard' ? (content.data.emailBusiness ?? '') : '',
	);
	const [vcardEmailPrivate, setVcardEmailPrivate] = useState(() =>
		content.type === 'vCard' ? (content.data.emailPrivate ?? '') : '',
	);
	const [vcardWebsite, setVcardWebsite] = useState(() =>
		content.type === 'vCard' ? (content.data.website ?? '') : '',
	);
	const [vcardStreet, setVcardStreet] = useState(() =>
		content.type === 'vCard' ? (content.data.streetBusiness ?? '') : '',
	);
	const [vcardCity, setVcardCity] = useState(() =>
		content.type === 'vCard' ? (content.data.cityBusiness ?? '') : '',
	);
	const [vcardZip, setVcardZip] = useState(() =>
		content.type === 'vCard' ? (content.data.zipBusiness ?? '') : '',
	);
	const [vcardCountry, setVcardCountry] = useState(() =>
		content.type === 'vCard' ? (content.data.countryBusiness ?? '') : '',
	);
	const [vcardNote, setVcardNote] = useState(() =>
		content.type === 'vCard' ? (content.data.note ?? '') : '',
	);
	const [vcardIsDynamic, setVcardIsDynamic] = useState(() =>
		content.type === 'vCard' ? (content.data.isDynamic ?? defaultDynamic) : defaultDynamic,
	);

	useEffect(() => {
		if (experience === 'website') {
			const valid =
				content.type === 'url' && parseCreatorDestination(experience, content.data.url) !== null;
			onValidityChange(valid);
		} else if (experience === 'whatsapp') {
			const valid =
				content.type === 'url' && parseCreatorDestination(experience, content.data.url) !== null;
			onValidityChange(valid);
		} else if (experience === 'wifi') {
			const valid = content.type === 'wifi' && content.data.ssid.trim().length > 0;
			onValidityChange(valid);
		} else if (experience === 'event') {
			const valid =
				content.type === 'event' &&
				content.data.title.trim().length > 0 &&
				new Date(content.data.startDate) < new Date(content.data.endDate);
			onValidityChange(valid);
		} else if (experience === 'vcard') {
			const valid =
				content.type === 'vCard' &&
				Object.entries(content.data).some(
					([k, v]) => k !== 'isDynamic' && typeof v === 'string' && v.trim().length > 0,
				);
			onValidityChange(valid);
		}
	}, [content, experience, onValidityChange]);

	const updateWebsite = (value: string, dynamic = isDynamic) => {
		setUrl(value);
		try {
			const nextContent = toQrContent({ experience: 'website', url: value, isDynamic: dynamic });
			updateContent(nextContent);
			setError('');
			onValidityChange(true);
		} catch (caught) {
			updateContent({ type: 'url', data: { url: value, isDynamic: dynamic } });
			setError(caught instanceof Error ? caught.message : 'Enter a valid website address');
			onValidityChange(false);
		}
	};

	const updateWhatsApp = (nextPhone: string, nextMessage: string, dynamic = isDynamic) => {
		setPhone(nextPhone);
		setMessage(nextMessage);
		try {
			const nextContent = toQrContent({
				experience: 'whatsapp',
				phone: nextPhone,
				message: nextMessage,
				isDynamic: dynamic,
			});
			updateContent(nextContent);
			setError('');
			onValidityChange(true);
		} catch (caught) {
			updateContent({ type: 'url', data: { url: '', isDynamic: dynamic } });
			setError(caught instanceof Error ? caught.message : 'Enter a valid WhatsApp number');
			onValidityChange(false);
		}
	};

	const updateWifi = (ssid: string, password = wifiPassword, encryption = wifiEncryption) => {
		setWifiSsid(ssid);
		setWifiPassword(password);
		setWifiEncryption(encryption);
		const valid = ssid.trim().length > 0;
		updateContent({
			type: 'wifi',
			data: { ssid: ssid.trim(), password, encryption },
		});
		onValidityChange(valid);
	};

	const updateEvent = (
		title: string,
		start = eventStartDate,
		end = eventEndDate,
		location = eventLocation,
		description = eventDescription,
	) => {
		setEventTitle(title);
		setEventStartDate(start);
		setEventEndDate(end);
		setEventLocation(location);
		setEventDescription(description);
		let valid = false;
		try {
			valid = title.trim().length > 0 && new Date(start) < new Date(end);
			if (valid) {
				updateContent({
					type: 'event',
					data: {
						title: title.trim(),
						startDate: new Date(start).toISOString(),
						endDate: new Date(end).toISOString(),
						location: location.trim() || undefined,
						description: description.trim() || undefined,
					},
				});
			}
		} catch {
			valid = false;
		}
		onValidityChange(valid);
	};

	const updateVCard = (
		patch: Partial<{
			firstName: string;
			lastName: string;
			photo: string;
			company: string;
			job: string;
			phoneMobile: string;
			phoneBusiness: string;
			emailBusiness: string;
			emailPrivate: string;
			website: string;
			street: string;
			city: string;
			zip: string;
			country: string;
			note: string;
			isDynamic: boolean;
		}>,
	) => {
		const next = {
			firstName: patch.firstName !== undefined ? patch.firstName : vcardFirstName,
			lastName: patch.lastName !== undefined ? patch.lastName : vcardLastName,
			photo: patch.photo !== undefined ? patch.photo : vcardPhoto,
			company: patch.company !== undefined ? patch.company : vcardCompany,
			job: patch.job !== undefined ? patch.job : vcardJob,
			phoneMobile: patch.phoneMobile !== undefined ? patch.phoneMobile : vcardPhoneMobile,
			phoneBusiness: patch.phoneBusiness !== undefined ? patch.phoneBusiness : vcardPhoneBusiness,
			emailBusiness: patch.emailBusiness !== undefined ? patch.emailBusiness : vcardEmailBusiness,
			emailPrivate: patch.emailPrivate !== undefined ? patch.emailPrivate : vcardEmailPrivate,
			website: patch.website !== undefined ? patch.website : vcardWebsite,
			streetBusiness: patch.street !== undefined ? patch.street : vcardStreet,
			cityBusiness: patch.city !== undefined ? patch.city : vcardCity,
			zipBusiness: patch.zip !== undefined ? patch.zip : vcardZip,
			countryBusiness: patch.country !== undefined ? patch.country : vcardCountry,
			note: patch.note !== undefined ? patch.note : vcardNote,
			isDynamic: patch.isDynamic !== undefined ? patch.isDynamic : vcardIsDynamic,
		};
		if (patch.firstName !== undefined) setVcardFirstName(patch.firstName);
		if (patch.lastName !== undefined) setVcardLastName(patch.lastName);
		if (patch.photo !== undefined) setVcardPhoto(patch.photo);
		if (patch.company !== undefined) setVcardCompany(patch.company);
		if (patch.job !== undefined) setVcardJob(patch.job);
		if (patch.phoneMobile !== undefined) setVcardPhoneMobile(patch.phoneMobile);
		if (patch.phoneBusiness !== undefined) setVcardPhoneBusiness(patch.phoneBusiness);
		if (patch.emailBusiness !== undefined) setVcardEmailBusiness(patch.emailBusiness);
		if (patch.emailPrivate !== undefined) setVcardEmailPrivate(patch.emailPrivate);
		if (patch.website !== undefined) setVcardWebsite(patch.website);
		if (patch.street !== undefined) setVcardStreet(patch.street);
		if (patch.city !== undefined) setVcardCity(patch.city);
		if (patch.zip !== undefined) setVcardZip(patch.zip);
		if (patch.country !== undefined) setVcardCountry(patch.country);
		if (patch.note !== undefined) setVcardNote(patch.note);
		if (patch.isDynamic !== undefined) setVcardIsDynamic(patch.isDynamic);

		const nextContent = toQrContent({
			experience: 'vcard',
			...next,
		});
		updateContent(nextContent);

		const valid = Object.entries(next).some(
			([k, v]) => k !== 'isDynamic' && typeof v === 'string' && v.trim().length > 0,
		);
		onValidityChange(valid);
	};

	return (
		<section aria-labelledby="content-step-title" className="space-y-6">
			<div>
				<h1 id="content-step-title" className="text-2xl font-bold tracking-tight text-slate-950">
					{experience === 'website' && 'Add your website'}
					{experience === 'whatsapp' && 'Set up WhatsApp'}
					{experience === 'wifi' && 'Set up Wi-Fi Network'}
					{experience === 'event' && 'Create Calendar Event'}
					{experience === 'vcard' && 'Create Contact Card (vCard)'}
				</h1>
				<p className="mt-2 text-slate-600">
					{experience === 'website' && 'Enter the page visitors should reach after scanning.'}
					{experience === 'whatsapp' && 'Enter the WhatsApp number customers should message.'}
					{experience === 'wifi' &&
						'Allow visitors to connect to your Wi-Fi instantly by scanning.'}
					{experience === 'event' &&
						'Share event details and allow attendees to add it to their calendar.'}
					{experience === 'vcard' &&
						'Enter your contact information to allow people to save you directly to their phone contacts.'}
				</p>
			</div>

			{/* Website Flow */}
			{experience === 'website' && (
				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="website-url">Website URL</Label>
						<Input
							id="website-url"
							name="websiteUrl"
							type="url"
							inputMode="url"
							autoComplete="url"
							maxLength={1000}
							placeholder="https://example.com…"
							value={url}
							aria-invalid={!!error}
							aria-describedby={error ? 'destination-error' : 'website-help'}
							onChange={(event) => updateWebsite(event.target.value)}
							onBlur={() => {
								try {
									const normalized = normalizeWebsiteUrl(url);
									setUrl(normalized);
									updateWebsite(normalized);
								} catch {
									// keep raw input for editing
								}
							}}
						/>
						<p id="website-help" className="text-sm text-slate-500">
							We’ll add https:// automatically if you leave off the prefix.
						</p>
					</div>
					{error && (
						<p
							id="destination-error"
							role="status"
							aria-live="polite"
							className="text-sm text-red-600"
						>
							{error}
						</p>
					)}
					<label
						htmlFor="website-dynamic"
						className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-4"
					>
						<input
							id="website-dynamic"
							name="websiteDynamic"
							type="checkbox"
							checked={isDynamic}
							onChange={(event) => {
								const checked = event.target.checked;
								setIsDynamic(checked);
								if (url.trim()) updateWebsite(url, checked);
							}}
							className="mt-1 size-4 accent-slate-900"
						/>
						<span>
							<span className="block font-medium">Dynamic QR code</span>
							<span className="mt-1 block text-sm text-slate-600">
								Change the destination later and track scans. Static codes point directly to the
								address and cannot be changed after printing.
							</span>
						</span>
					</label>
				</div>
			)}

			{/* WhatsApp Flow */}
			{experience === 'whatsapp' && (
				<div className="space-y-5">
					<div className="space-y-2">
						<Label htmlFor="whatsapp-phone">WhatsApp phone number</Label>
						<Input
							id="whatsapp-phone"
							name="whatsappPhone"
							type="tel"
							inputMode="tel"
							autoComplete="tel"
							maxLength={30}
							placeholder="+234 803 000 0000…"
							value={phone}
							aria-invalid={!!error}
							aria-describedby={error ? 'destination-error' : 'phone-help'}
							onChange={(event) => updateWhatsApp(event.target.value, message)}
						/>
						<p id="phone-help" className="text-sm text-slate-500">
							Include the country calling code, for example +234.
						</p>
					</div>
					<div className="space-y-2">
						<Label htmlFor="whatsapp-message">
							Opening message <span className="font-normal text-slate-500">(optional)</span>
						</Label>
						<Textarea
							id="whatsapp-message"
							name="whatsappMessage"
							maxLength={1000}
							autoComplete="off"
							value={message}
							placeholder="Hi, I found you through your QR code."
							onChange={(event) => updateWhatsApp(phone, event.target.value)}
							className="min-h-28"
						/>
						<p className="text-right text-xs text-slate-500">{message.length}/1,000</p>
					</div>
					{error && (
						<p
							id="destination-error"
							role="status"
							aria-live="polite"
							className="text-sm text-red-600"
						>
							{error}
						</p>
					)}
					<label
						htmlFor="whatsapp-dynamic"
						className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-4"
					>
						<input
							id="whatsapp-dynamic"
							name="whatsappDynamic"
							type="checkbox"
							checked={isDynamic}
							onChange={(event) => {
								const checked = event.target.checked;
								setIsDynamic(checked);
								if (phone.trim()) updateWhatsApp(phone, message, checked);
							}}
							className="mt-1 size-4 accent-slate-900"
						/>
						<span>
							<span className="block font-medium">Dynamic QR code</span>
							<span className="mt-1 block text-sm text-slate-600">
								Change the number or opening message later and track scans.
							</span>
						</span>
					</label>
				</div>
			)}

			{/* Wi-Fi Flow */}
			{experience === 'wifi' && (
				<div className="space-y-5">
					<div className="space-y-2">
						<Label htmlFor="wifi-ssid">
							Network Name (SSID) <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="wifi-ssid"
							name="wifiSsid"
							maxLength={32}
							placeholder="e.g. Cafe_Guest_WiFi"
							value={wifiSsid}
							onChange={(e) => updateWifi(e.target.value)}
							className="h-10 text-sm font-medium"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="wifi-password">Network Password</Label>
						<Input
							id="wifi-password"
							name="wifiPassword"
							type="password"
							maxLength={64}
							placeholder="Leave blank for open networks"
							value={wifiPassword}
							onChange={(e) => updateWifi(wifiSsid, e.target.value)}
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="wifi-encryption">Encryption Security</Label>
						<select
							id="wifi-encryption"
							value={wifiEncryption}
							onChange={(e) =>
								updateWifi(wifiSsid, wifiPassword, e.target.value as 'WPA' | 'WEP' | 'nopass')
							}
							className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
						>
							<option value="WPA">WPA / WPA2 / WPA3 (Standard)</option>
							<option value="WEP">WEP (Legacy)</option>
							<option value="nopass">None (Open Network)</option>
						</select>
					</div>

					<p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
						When guests scan this QR code, their phone will automatically prompt them to connect to
						your Wi-Fi network without typing the password.
					</p>
				</div>
			)}

			{/* Event Flow */}
			{experience === 'event' && (
				<div className="space-y-5">
					<div className="space-y-2">
						<Label htmlFor="event-title">
							Event Title <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="event-title"
							name="eventTitle"
							maxLength={200}
							placeholder="e.g. Annual Design Summit 2026"
							value={eventTitle}
							onChange={(e) => updateEvent(e.target.value)}
							className="h-10 text-sm font-medium"
						/>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="event-start">
								Start Date & Time <span className="text-rose-500">*</span>
							</Label>
							<Input
								id="event-start"
								type="datetime-local"
								value={eventStartDate}
								onChange={(e) => updateEvent(eventTitle, e.target.value)}
								className="h-10 text-sm"
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="event-end">
								End Date & Time <span className="text-rose-500">*</span>
							</Label>
							<Input
								id="event-end"
								type="datetime-local"
								value={eventEndDate}
								onChange={(e) => updateEvent(eventTitle, eventStartDate, e.target.value)}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="event-location">Location / Venue</Label>
						<Input
							id="event-location"
							maxLength={200}
							placeholder="e.g. Moscone Center, San Francisco, CA"
							value={eventLocation}
							onChange={(e) =>
								updateEvent(eventTitle, eventStartDate, eventEndDate, e.target.value)
							}
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="event-desc">Event Description</Label>
						<Textarea
							id="event-desc"
							maxLength={500}
							placeholder="Brief description of the schedule or keynote topics"
							value={eventDescription}
							onChange={(e) =>
								updateEvent(eventTitle, eventStartDate, eventEndDate, eventLocation, e.target.value)
							}
							rows={2}
							className="text-sm resize-none"
						/>
					</div>
				</div>
			)}

			{/* vCard Flow */}
			{experience === 'vcard' && (
				<div className="space-y-4">
					{/* Profile Photo Upload */}
					<div className="flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50/50">
						{vcardPhoto ? (
							<div className="relative group size-16 rounded-full overflow-hidden border-2 border-white shadow-xs shrink-0 bg-slate-100">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									src={vcardPhoto}
									alt="Profile photo preview"
									className="size-full object-cover"
								/>
								<button
									type="button"
									onClick={() => updateVCard({ photo: '' })}
									className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer rounded-full"
									title="Remove photo"
								>
									<Trash2 className="size-4" />
								</button>
							</div>
						) : (
							<div
								onClick={() => vcardPhotoInputRef.current?.click()}
								className="size-16 rounded-full border-2 border-dashed border-slate-300 hover:border-slate-400 bg-white flex flex-col items-center justify-center cursor-pointer transition text-slate-400 hover:text-slate-600 shrink-0"
								title="Upload contact photo"
							>
								<Upload className="size-5" />
							</div>
						)}
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<span className="text-xs font-bold text-slate-900">Profile Photo</span>
								<span className="text-[10px] text-slate-500 font-medium">(optional)</span>
							</div>
							<p className="text-xs text-slate-500 mt-0.5">
								Add a personal photo or headshot to your contact card (PNG or JPEG, up to 1 MB).
							</p>
							<div className="mt-1.5 flex items-center gap-2">
								<button
									type="button"
									onClick={() => vcardPhotoInputRef.current?.click()}
									className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
								>
									{vcardPhoto ? 'Change Photo' : 'Upload Photo'}
								</button>
								{vcardPhoto && (
									<>
										<span className="text-slate-300">•</span>
										<button
											type="button"
											onClick={() => updateVCard({ photo: '' })}
											className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
										>
											Remove
										</button>
									</>
								)}
							</div>
							<input
								type="file"
								ref={vcardPhotoInputRef}
								accept="image/png,image/jpeg,image/webp"
								className="hidden"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (!file) return;
									if (file.size > 1024 * 1024) {
										alert('Image must be under 1MB');
										return;
									}
									const reader = new FileReader();
									reader.onload = () => {
										if (typeof reader.result === 'string') {
											updateVCard({ photo: reader.result });
										}
									};
									reader.readAsDataURL(file);
								}}
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="vcard-first">First Name</Label>
							<Input
								id="vcard-first"
								maxLength={64}
								placeholder="e.g. Sarah"
								value={vcardFirstName}
								onChange={(e) => updateVCard({ firstName: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-last">Last Name</Label>
							<Input
								id="vcard-last"
								maxLength={64}
								placeholder="e.g. Jenkins"
								value={vcardLastName}
								onChange={(e) => updateVCard({ lastName: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="vcard-company">Company / Organization</Label>
							<Input
								id="vcard-company"
								maxLength={64}
								placeholder="e.g. Acme Studio"
								value={vcardCompany}
								onChange={(e) => updateVCard({ company: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-job">Job Title</Label>
							<Input
								id="vcard-job"
								maxLength={64}
								placeholder="e.g. Creative Director"
								value={vcardJob}
								onChange={(e) => updateVCard({ job: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="vcard-mobile">Mobile Phone</Label>
							<Input
								id="vcard-mobile"
								type="tel"
								maxLength={30}
								placeholder="e.g. +1 555 123 4567"
								value={vcardPhoneMobile}
								onChange={(e) => updateVCard({ phoneMobile: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-work-phone">Work Phone</Label>
							<Input
								id="vcard-work-phone"
								type="tel"
								maxLength={30}
								placeholder="e.g. +1 555 987 6543"
								value={vcardPhoneBusiness}
								onChange={(e) => updateVCard({ phoneBusiness: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="vcard-work-email">Work Email</Label>
							<Input
								id="vcard-work-email"
								type="email"
								maxLength={100}
								placeholder="sarah@acmestudio.com"
								value={vcardEmailBusiness}
								onChange={(e) => updateVCard({ emailBusiness: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-personal-email">Personal Email</Label>
							<Input
								id="vcard-personal-email"
								type="email"
								maxLength={100}
								placeholder="sarah.jenkins@gmail.com"
								value={vcardEmailPrivate}
								onChange={(e) => updateVCard({ emailPrivate: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="vcard-website">Website URL</Label>
						<Input
							id="vcard-website"
							type="url"
							maxLength={200}
							placeholder="https://acmestudio.com"
							value={vcardWebsite}
							onChange={(e) => updateVCard({ website: e.target.value })}
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="vcard-street">Street Address</Label>
						<Input
							id="vcard-street"
							maxLength={100}
							placeholder="e.g. 100 Innovation Way"
							value={vcardStreet}
							onChange={(e) => updateVCard({ street: e.target.value })}
							className="h-10 text-sm"
						/>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						<div className="space-y-2">
							<Label htmlFor="vcard-city">City</Label>
							<Input
								id="vcard-city"
								maxLength={64}
								placeholder="e.g. San Francisco"
								value={vcardCity}
								onChange={(e) => updateVCard({ city: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-zip">ZIP / Postal Code</Label>
							<Input
								id="vcard-zip"
								maxLength={20}
								placeholder="e.g. 94105"
								value={vcardZip}
								onChange={(e) => updateVCard({ zip: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="vcard-country">Country</Label>
							<Input
								id="vcard-country"
								maxLength={64}
								placeholder="e.g. United States"
								value={vcardCountry}
								onChange={(e) => updateVCard({ country: e.target.value })}
								className="h-10 text-sm"
							/>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="vcard-note">Note</Label>
						<Textarea
							id="vcard-note"
							maxLength={300}
							placeholder="Additional contact notes or office hours"
							value={vcardNote}
							onChange={(e) => updateVCard({ note: e.target.value })}
							rows={2}
							className="text-sm resize-none"
						/>
					</div>

					<div className="pt-3 flex items-center justify-between border-t border-slate-200">
						<div>
							<Label
								htmlFor="vcard-dynamic"
								className="text-sm font-semibold text-slate-900 block cursor-pointer"
							>
								Dynamic QR Code
							</Label>
							<p className="text-xs text-slate-500">
								Edit details anytime without reprinting, and track scans in analytics.
							</p>
						</div>
						<input
							type="checkbox"
							id="vcard-dynamic"
							checked={vcardIsDynamic}
							onChange={(e) => updateVCard({ isDynamic: e.target.checked })}
							className="size-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
						/>
					</div>
				</div>
			)}
		</section>
	);
}
